import { api } from '../api.js';
import { state } from '../state.js';
import { navigate, route, RouterLink } from '../router.js';
import { Markdown } from '../ui.js';
import { VoiceInput, heardHeader, voiceSupported } from '../speech.js';

// Обсуждение с учителем — отдельный экран, как и сам собес.
//
// Сначала это была панель под разбором. Разговор в панели неудобен: реплики
// длинные, поле ввода уезжает за край, а страница разбора продолжает жить
// своей жизнью и скачет под руками. Разговор — это разговор, ему нужен весь
// экран и своя прокрутка.
export const TalkPage = {
  components: { RouterLink, Markdown },
  data() {
    return {
      talk: null, turns: [],
      draft: '', status: '', error: '',
      thinking: false, loading: true,
      listening: false, level: [], clip: 0,
      voiceIn: null, playing: 0, audio: null,
    };
  },
  computed: {
    voiceSupported: () => voiceSupported,
    /** Куда возвращаться: к разбору собеса или к карточкам проекта. */
    back() {
      if (this.talk?.session_id) return `/session/${this.talk.session_id}`;
      return `/project/${state.projectId}/cards`;
    },
    title() { return this.talk?.title || 'Обсуждение'; },
  },
  async created() {
    this.voiceIn = new VoiceInput(
      (bands) => { this.level = bands; },
      (m) => { this.status = m; });
    await this.load();
  },
  beforeUnmount() { this.voiceIn?.stop(); this.hush(); },
  methods: {
    async load() {
      try {
        const id = Number(route.params.id);
        const { talk, turns } = await api.get(`/api/talks/${state.projectId}/talk/${id}`);
        this.talk = talk;
        this.turns = turns;
      } catch (e) { this.error = e.message; }
      finally { this.loading = false; this.$nextTick(() => this.down()); }
    },

    down() {
      const el = this.$refs.feed;
      if (el) el.scrollTop = el.scrollHeight;
    },

    async send() {
      const text = this.draft.trim();
      if (!text || this.thinking || !this.talk) return;
      this.draft = '';
      this.error = '';
      this.thinking = true;
      this.status = 'думает…';
      // Свою реплику показываем сразу: ответ идёт секунды, и пустой экран всё
      // это время читается как «ничего не отправилось».
      this.turns = [...this.turns,
        { id: `local-${Date.now()}`, role: 'user', body: text, clip_id: this.clip || null }];
      this.$nextTick(() => this.down());
      const clip = this.clip;
      this.clip = 0;
      try {
        const { turns } = await api.post(`/api/talks/${state.projectId}/talk/${this.talk.id}`,
          { text, key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
            ...(clip ? { clip } : {}) });
        this.turns = turns;
        this.status = '';
      } catch (e) {
        this.turns = this.turns.filter((t) => String(t.id).indexOf('local-') !== 0);
        this.draft = text;
        this.error = e.message;
        this.status = '';
      } finally {
        this.thinking = false;
        this.$nextTick(() => this.down());
      }
    },

    async mic() {
      if (this.listening) return this.stopMic();
      this.hush();
      this.listening = true;
      await this.voiceIn.start();
      this.status = 'слушаю — договаривай и жми «Стоп»';
    },

    async stopMic() {
      this.listening = false;
      this.level = [];
      const said = await this.voiceIn.stop();
      if (!said) { this.status = ''; return; }
      this.status = 'распознаю…';
      try {
        const { text, clip } = await api.raw('/api/media/recognize', said.blob,
          heardHeader(said.heard));
        if (clip) this.clip = clip;
        if (text) {
          const had = this.draft.trim();
          this.draft = had ? `${had} ${text}` : text;
          this.status = 'распознал — проверь и отправляй';
        } else this.status = 'ничего не расслышал';
      } catch (e) { this.status = e.message; }
    },

    /** Свой голос — переслушать: слышно то, чего не видно в расшифровке. */
    play(turn) {
      if (this.playing === turn.clip_id) return this.hush();
      this.hush();
      this.playing = turn.clip_id;
      this.audio = new Audio(`/api/media/clips/${turn.clip_id}`);
      this.audio.onended = () => this.hush();
      this.audio.play().catch(() => this.hush());
    },

    /** Ответ учителя вслух: длинное объяснение глазами читать хочется не всегда. */
    say(turn) {
      if (this.playing === turn.id) return this.hush();
      this.hush();
      this.playing = turn.id;
      this.audio = new Audio(`/api/media/tts?text=${encodeURIComponent(turn.body.slice(0, 3000))}`);
      this.audio.onended = () => this.hush();
      this.audio.play().catch(() => this.hush());
    },

    hush() {
      if (this.audio) { this.audio.pause(); this.audio.src = ''; }
      this.audio = null;
      this.playing = 0;
    },

    leave() { navigate(this.back); },
  },
  template: `
    <div class="interview talkpage">
      <header>
        <a class="back" @click.prevent="leave" href="#">← назад</a>
        <span class="tag">{{ title }}</span>
        <span class="spacer"></span>
        <span class="faint">учитель, не интервьюер</span>
      </header>

      <div class="thread" ref="feed">
        <div class="thread-inner">
          <p class="err" v-if="error">{{ error }}</p>
          <p class="faint" v-if="loading">открываю…</p>
          <p class="faint" v-else-if="!turns.length">
            Спроси что угодно по этому разбору: объяснит по делу, без наводящих
            вопросов и без оценок.
          </p>
          <div v-for="t in turns" :key="t.id" class="bubble" :class="t.role">
            <Markdown v-if="t.role === 'teacher'" :text="t.body" />
            <template v-else>{{ t.body }}</template>
            <div class="talk-actions">
              <button class="link" v-if="t.clip_id" @click="play(t)">
                {{ playing === t.clip_id ? 'стоп' : 'мой голос' }}
              </button>
              <button class="link" v-if="t.role === 'teacher'" @click="say(t)">
                {{ playing === t.id ? 'стоп' : 'вслух' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="composer">
        <div class="composer-inner">
          <textarea v-model="draft" rows="2" @keydown.ctrl.enter="send"
                    :placeholder="listening ? 'можно и напечатать — запись идёт'
                                            : 'что объяснить?'"></textarea>
          <div class="row">
            <button class="mic" v-if="voiceSupported" :class="{ recording: listening }"
                    @click="mic">{{ listening ? 'Стоп' : 'Голосом' }}</button>
            <span class="faint" v-if="status">{{ status }}</span>
            <span class="spacer"></span>
            <button class="go" :disabled="thinking || !draft.trim()" @click="send">
              Спросить
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
};
